import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Pencil, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { type ProductRecord, type ProductStatus, useUpdateProduct } from "~/lib/products";

const STATUSES: ReadonlyArray<{ value: ProductStatus; label: string }> = [
    { value: "active", label: "Active" },
    { value: "deprecated", label: "Deprecated" },
    { value: "retired", label: "Retired" },
];

const modalStyle: React.CSSProperties = { padding: "var(--space-6)" };

interface Props {
    product: ProductRecord | null;
    open: boolean;
    onOpenChange(open: boolean): void;
}

export function EditProductDialog({ product, open, onOpenChange }: Props): React.ReactElement {
    const [name, setName] = useState("");
    const [vendor, setVendor] = useState("");
    const [description, setDescription] = useState("");
    const [icon, setIcon] = useState("");
    const [status, setStatus] = useState<ProductStatus>("active");
    const [error, setError] = useState<string | null>(null);

    const update = useUpdateProduct();

    useEffect(() => {
        if (product && open) {
            setName(product.name);
            setVendor(product.vendor);
            setDescription(product.description);
            setIcon(product.icon ?? "");
            setStatus(product.status);
            setError(null);
            update.reset();
        }
    }, [product, open, update]);

    async function onSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        if (!product) return;
        setError(null);
        if (!name.trim()) {
            setError("Name required.");
            return;
        }
        try {
            await update.mutateAsync({
                slug: product.slug,
                name: name.trim(),
                vendor: vendor.trim(),
                description,
                status,
                ...(icon.trim() === "" ? { clearIcon: true } : { icon: icon.trim() }),
            });
            toast.success("Product updated");
            onOpenChange(false);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to update product.");
        }
    }

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content dialog-content--md enter-up" style={modalStyle}>
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">
                                <span className="cluster-xs">
                                    <Pencil size={16} aria-hidden />
                                    <span>Edit product</span>
                                </span>
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>
                        <Dialog.Description className="text-text-muted text-12" style={{ marginTop: "var(--space-2)" }}>
                            Slug and metering model are immutable.
                        </Dialog.Description>

                        <form onSubmit={onSubmit} className="stack" style={{ marginTop: "var(--space-4)" }}>
                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Name</span>
                                <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
                            </label>
                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Vendor</span>
                                <input className="input" value={vendor} onChange={(e) => setVendor(e.target.value)} />
                            </label>
                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Description</span>
                                <textarea
                                    className="input"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </label>
                            <div className="cluster" style={{ gap: "var(--space-3)" }}>
                                <label className="stack-xs" style={{ flex: 1 }}>
                                    <span className="text-12 text-text-muted">Icon</span>
                                    <input
                                        className="input"
                                        value={icon}
                                        onChange={(e) => setIcon(e.target.value)}
                                        placeholder="shield"
                                    />
                                </label>
                                <label className="stack-xs" style={{ flex: 1 }}>
                                    <span className="text-12 text-text-muted">Status</span>
                                    <select
                                        className="input"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value as ProductStatus)}
                                    >
                                        {STATUSES.map((s) => (
                                            <option key={s.value} value={s.value}>
                                                {s.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                            {error && (
                                <div
                                    className="chip chip--danger shake"
                                    style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}
                                    role="alert"
                                >
                                    {error}
                                </div>
                            )}
                            <button type="submit" className="btn btn--primary press-crush" disabled={update.isPending}>
                                {update.isPending ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />}
                                <span>{update.isPending ? "Saving…" : "Save changes"}</span>
                            </button>
                        </form>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
